/**
 * Data-quality access (ADR-0010): findings, capability trust with its episodes, device health,
 * installation validation and AI audits. TanStack Query hooks over the typed client.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import type { ActionOut } from './types';

export const qualityKeys = {
  all: ['quality'] as const,
  overview: (p: object) => ['quality', 'overview', p] as const,
  checks: ['quality', 'checks'] as const,
  findings: (p: object) => ['quality', 'findings', p] as const,
  finding: (id: string) => ['quality', 'finding', id] as const,
  trust: (p: object) => ['quality', 'trust', p] as const,
  vehicleTrust: (id: string) => ['quality', 'vehicle-trust', id] as const,
  health: (p: object) => ['quality', 'health', p] as const,
  deviceHealth: (id: string) => ['quality', 'device-health', id] as const,
  installations: (id: string) => ['quality', 'installations', id] as const,
  audits: ['quality', 'audits'] as const,
  audit: (id: string) => ['quality', 'audit', id] as const,
};

export function useQualityOverview(project: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.overview({ project }),
    queryFn: () => unwrap(api.GET('/api/quality/overview', { params: { query: { project } } })),
    enabled,
  });
}

export function useChecks(enabled = true) {
  return useQuery({ queryKey: qualityKeys.checks, queryFn: () => unwrap(api.GET('/api/quality/checks')), staleTime: 60 * 60_000, enabled });
}

export interface FindingQuery {
  status?: string[];
  source?: string;
  severity?: string[];
  vehicle?: string;
  device?: string;
  check?: string;
  project?: string[];
}

export function useFindings(q: FindingQuery, enabled = true, limit = 100) {
  return useInfiniteQuery({
    queryKey: qualityKeys.findings({ ...q, limit }),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/quality/findings', { params: { query: { ...q, cursor: pageParam, limit } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useFinding(id: string | undefined) {
  return useQuery({
    queryKey: qualityKeys.finding(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/quality/findings/{finding_id}', { params: { path: { finding_id: id ?? '' } } })),
    enabled: !!id,
  });
}

function useAction<V>(fn: (v: V) => Promise<ActionOut>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: qualityKeys.all }) });
}

/** acknowledge (open → acknowledged), dismiss (not a real problem), confirm (an AI proposal is real). */
export function useDecideFinding() {
  return useAction((v: { id: string; verb: 'acknowledge' | 'dismiss' | 'confirm'; note: string; version: number }) => {
    const opts = { params: { path: { finding_id: v.id } }, body: { note: v.note, version: v.version } };
    if (v.verb === 'acknowledge') return unwrap(api.POST('/api/quality/findings/{finding_id}/acknowledge', opts));
    if (v.verb === 'dismiss') return unwrap(api.POST('/api/quality/findings/{finding_id}/dismiss', opts));
    return unwrap(api.POST('/api/quality/findings/{finding_id}/confirm', opts));
  });
}

export function useTrust(q: { state?: string[]; capability?: string; vehicle?: string }, enabled = true) {
  return useInfiniteQuery({
    queryKey: qualityKeys.trust(q),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/quality/trust', { params: { query: { ...q, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useVehicleTrust(vehicleId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.vehicleTrust(vehicleId ?? ''),
    queryFn: () => unwrap(api.GET('/api/quality/vehicles/{vehicle_id}/trust', { params: { path: { vehicle_id: vehicleId ?? '' } } })),
    enabled: enabled && !!vehicleId,
  });
}

export function useRunChecks(vehicleId: string) {
  return useAction((day: string | null) =>
    unwrap(api.POST('/api/quality/vehicles/{vehicle_id}/check', { params: { path: { vehicle_id: vehicleId } }, body: { day } })),
  );
}

export function useDevicesHealth(q: { project?: string[]; status: 'all' | 'issues' | 'ok'; q?: string }, enabled = true) {
  return useInfiniteQuery({
    queryKey: qualityKeys.health(q),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/quality/devices/health', { params: { query: { ...q, cursor: pageParam, limit: 100 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDeviceHealth(deviceId: string) {
  return useQuery({
    queryKey: qualityKeys.deviceHealth(deviceId),
    queryFn: () => unwrap(api.GET('/api/quality/devices/{device_id}/health', { params: { path: { device_id: deviceId } } })),
    refetchInterval: 60_000,
  });
}

export function useInstallations(deviceId: string) {
  return useQuery({
    queryKey: qualityKeys.installations(deviceId),
    queryFn: () => unwrap(api.GET('/api/quality/devices/{device_id}/installations', { params: { path: { device_id: deviceId }, query: { limit: 20 } } })),
  });
}

export function useValidateInstallation(deviceId: string) {
  return useAction((body: { window_hours?: number | null; note: string }) =>
    unwrap(api.POST('/api/quality/devices/{device_id}/validate-installation', { params: { path: { device_id: deviceId } }, body })),
  );
}

export function useAudits(enabled = true) {
  return useInfiniteQuery({
    queryKey: qualityKeys.audits,
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/quality/audits', { params: { query: { cursor: pageParam, limit: 50 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
}

export function useAudit(id: string) {
  return useQuery({
    queryKey: qualityKeys.audit(id),
    queryFn: () => unwrap(api.GET('/api/quality/audits/{audit_id}', { params: { path: { audit_id: id } } })),
  });
}

export function useRunAudit() {
  return useAction((reason: string) => unwrap(api.POST('/api/quality/audits', { body: { reason } })));
}
